import { useState, useEffect, useCallback, useRef, useContext } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useLocation } from 'react-router-dom'
import CreateJs from 'preload-js'
import { Context } from '@/context'
import { Context as ScrollbarContext } from '@/context/scrollbar'
import gsap from 'gsap'
import ScrollToPlugin from 'gsap/ScrollToPlugin'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import BreadcrumbNav from '@/components/BreadcrumbNav'
import CoverProduct from '@/components/CoverProduct'
import WpContactForm7 from '@/components/WpContactForm7'
import Footer from '@/components/Footer'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import * as formActions from '@/actions/form'
import { getSlug } from '@/utils/path'
import { removeParagraph } from '@/utils/strings'
import style from './style'

const useStyles = createUseStyles(style)

gsap.registerPlugin(ScrollToPlugin)

const Vantaggi = () => {
  const { getImageUrl, setPageAnimationReady, headerHeight } = useContext(Context)
  const { scrollbar } = useContext(ScrollbarContext)
  const classes = useStyles({ headerHeight })
  const [load, setLoad] = useState(false)
  const preload = useRef(new CreateJs.LoadQueue())
  const $root = useRef()
  const formId = useRef()
  const location = useLocation()
  const stub = useRef(getSlug(location.pathname))

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, currentForm, strings } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub.current] || {},
    mediaQueryKey: state.mediaQuery.key,
    currentForm: state.form[formId.current] || [],
    strings: state.language.string,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])
  const fetchForm = useCallback((id) => dispatch(formActions.fetchForm(id)), [dispatch])
  const sendContactForm = useCallback((id, data, isMultipart) => dispatch(formActions.sendContactForm(id, data, isMultipart)), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  If Form does not exist call API form
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) {
      formId.current = page.form_banner_form_id
      fetchForm(formId.current)
    }
  }, [page])

  /*------------------------------
  Preload Main Images: preloading only thre three first image
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const heroMedia = getImageUrl(page.featured_media, 'full', stub.current, 'pages')
    if (page.featured_media !== 0) preload.current.loadFile(heroMedia)
  }

  /*------------------------------
  Initialize (API Calls)
  ------------------------------*/
  const initialize = () => {
    if (current !== stub.current) setCurrentPage(stub.current, 'pages')
    if (Object.keys(page).length === 0) getPage(stub.current)
  }

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  /*------------------------------
  If there are products in category complete the preload
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) preloadMedia()
  }, [page])

  /*------------------------------
  Go to appointement form
  ------------------------------*/
  const goToAppointmentForm = () => {
    scrollbar.scrollTo('#appointment')
  }

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render Breadcrumb
  ------------------------------*/
  const renderBreadcrumb = () => {
    return (
      <div className={classes.wrapper}>
        <BreadcrumbNav back />
      </div>
    )
  }

  /*------------------------------
  Render Cover
  ------------------------------*/
  const renderCover = () => {
    return (
      <div className={`${classes.wrapper} ${classes.coverProduct}`}>
        <CoverProduct
          title={page.title.rendered}
          text={page.excerpt.rendered}
          img={getImageUrl(page.featured_media, 'full', stub.current)}
          onBookClick={goToAppointmentForm}
        />
      </div>
    )
  }

  /*------------------------------
  Render List
  ------------------------------*/
  const renderList = () => {
    return page.lists_items.length && (
      <div className={`${classes.wrapper} ${classes.warranty}`}>
        <ol className={classes.warrantyList}>
          {page.lists_items.map((item, i) => (
            <li key={i.toString()}>
              <h3>{item.list_title}</h3>
              <div
                className={classes.warrantyListText}
                dangerouslySetInnerHTML={{ __html: item.list_text }}
              />
            </li>
          ))}
        </ol>
      </div>
    )
  }

  const renderGrid = (children) => {
    return (
      <div
        id="appointment"
        className={`${classes.wrapper} ${classes.lightBackground} ${classes.appointment}`}
      >
        <div className={classes.left}>
          <div className={classes.leftTitle}>
            <h3 dangerouslySetInnerHTML={{ __html: removeParagraph(page.form_banner_title) }} />
          </div>
          <svg className={classes.leftNumber}><use xlinkHref="#ico-numero-verde" /></svg>
          <div className={classes.leftText}>
            <div dangerouslySetInnerHTML={{ __html: page.form_banner_text }} />
          </div>
        </div>
        <div className={classes.right}>
          {children}
        </div>
      </div>
    )
  }

  /*------------------------------
  Render Form
  ------------------------------*/
  const renderForm = () => {
    return (
      <div className={`${classes.formContent} ${classes.formContentCenter}`}>
        <div className={classes.pretitle}>{page.contact_form_title}</div>
        <WpContactForm7
          className={classes.form}
          fields={currentForm}
          formID={formId.current}
          sendContactForm={sendContactForm}
          submitLabel={strings.send_label}
        />
      </div>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => <Footer background="light" />

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <div className={classes.pageContent}>
        {renderBreadcrumb()}
        {renderCover()}
        {renderList()}
        {renderGrid(renderForm())}
        {renderFooter()}
      </div>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageVantaggi ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default Vantaggi
